import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useApp } from "../context/AppContext";
import { rupiah } from "../lib/format";
import { RupiahInput } from "../components/RupiahInput";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, Pencil, Trash2, Search, Tags, Layers, DollarSign, AlertTriangle, Upload, Download, Loader2, Power, Check, X } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { barcode: "", name: "", unit: "", category: "", subcategory: "", location: "", price_buy: 0, price_sell: 0, stock: 0, min_stock: 0, active: true };

export default function Items() {
  const { isViewOnly, can } = useAuth();
  const isMaster = can("master");
  const { refreshLowStock } = useApp();
  const [items, setItems] = useState([]);
  const [md, setMd] = useState({ units: [], categories: [], subcategories: [], locations: [] });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [subFilter, setSubFilter] = useState("all");
  const [stockFilter, setStockFilter] = useState("all");
  const [form, setForm] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [classOpen, setClassOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const load = () => api.get("/items").then((r) => setItems(r.data));
  const loadMd = () => Promise.all(["units", "categories", "subcategories", "locations"].map((c) => api.get(`/${c}`)))
    .then(([u, c, s, l]) => setMd({ units: u.data, categories: c.data, subcategories: s.data, locations: l.data }));
  useEffect(() => { load(); loadMd(); }, []);

  const filtered = items.filter((i) => {
    const q = search.toLowerCase();
    const match = i.name.toLowerCase().includes(q) || i.barcode.toLowerCase().includes(q);
    const st = statusFilter === "all" || (statusFilter === "active" ? i.active : !i.active);
    const sub = subFilter === "all" || i.subcategory === subFilter;
    const low = stockFilter === "all" || (i.stock <= i.min_stock);
    return match && st && sub && low;
  });

  const save = async () => {
    try {
      if (form.id) await api.put(`/items/${form.id}`, form);
      else await api.post("/items", form);
      toast.success("Barang tersimpan");
      setForm(null); load(); refreshLowStock();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Hapus barang ini?")) return;
    await api.delete(`/items/${id}`); load(); refreshLowStock();
  };
  const toggleActive = async (item) => {
    const next = !item.active;
    if (!window.confirm(`${next ? "Aktifkan" : "Nonaktifkan"} barang "${item.name}"?`)) return;
    try {
      await api.put(`/items/${item.id}`, { ...item, active: next });
      toast.success(next ? "Barang diaktifkan" : "Barang dinonaktifkan");
      load(); refreshLowStock();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="items-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold font-display text-slate-900">Master Barang</h1>
          <p className="text-sm text-slate-500">Katalog barang dengan klasifikasi & kontrol stok</p>
        </div>
        {!isViewOnly && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-items-button">
              <Upload className="h-4 w-4 mr-1.5" /> Impor Barang
            </Button>
            <Button variant="outline" onClick={() => setClassOpen(true)} data-testid="manage-classification-button">
              <Layers className="h-4 w-4 mr-1.5" /> Klasifikasi
            </Button>
            <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="bulk-price-button">
              <DollarSign className="h-4 w-4 mr-1.5" /> Update Harga Massal
            </Button>
            <Button onClick={() => setForm({ ...EMPTY })} className="bg-orange-500 hover:bg-orange-600" data-testid="add-item-button">
              <Plus className="h-4 w-4 mr-1.5" /> Tambah Barang
            </Button>
          </div>
        )}
      </div>

      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari nama / barcode..." className="pl-9" data-testid="items-search-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="status-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            <SelectItem value="active">Aktif</SelectItem>
            <SelectItem value="inactive">Nonaktif</SelectItem>
          </SelectContent>
        </Select>
        <Select value={subFilter} onValueChange={setSubFilter}>
          <SelectTrigger className="w-48" data-testid="subcategory-filter-select"><SelectValue placeholder="Sub-Kategori" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Sub-Kategori</SelectItem>
            {md.subcategories.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={stockFilter} onValueChange={setStockFilter}>
          <SelectTrigger className="w-44" data-testid="stock-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Stok</SelectItem>
            <SelectItem value="low">Stok Minimum</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Barcode</TableHead><TableHead>Nama</TableHead><TableHead>Kategori</TableHead>
              <TableHead className="text-right">Harga Beli</TableHead><TableHead className="text-right">Harga Jual</TableHead>
              <TableHead className="text-right">Stok</TableHead><TableHead>Status</TableHead>
              {!isViewOnly && <TableHead className="text-right">Aksi</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((i) => (
              <TableRow key={i.id} data-testid={`item-row-${i.barcode}`}>
                <TableCell className="font-mono text-xs">{i.barcode}</TableCell>
                <TableCell className="font-medium">{i.name}</TableCell>
                <TableCell className="text-xs text-slate-500">{i.category} / {i.subcategory}</TableCell>
                <TableCell className="text-right mono text-slate-500">{rupiah(i.price_buy)}</TableCell>
                <TableCell className="text-right mono font-semibold">{rupiah(i.price_sell)}</TableCell>
                <TableCell className="text-right">
                  <span className={`mono ${i.stock <= i.min_stock ? "text-rose-600 font-bold" : ""}`}>
                    {i.stock <= i.min_stock && <AlertTriangle className="h-3 w-3 inline mr-1" />}
                    {i.stock} {i.unit}
                  </span>
                </TableCell>
                <TableCell>
                  {isMaster ? (
                    <button
                      type="button"
                      onClick={() => toggleActive(i)}
                      data-testid={`toggle-active-${i.barcode}`}
                      title="Klik untuk ubah status (Master)"
                      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors cursor-pointer ${i.active ? "border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100" : "border-slate-300 text-slate-500 bg-slate-50 hover:bg-slate-100"}`}
                    >
                      <Power className="h-3 w-3" /> {i.active ? "Aktif" : "Nonaktif"}
                    </button>
                  ) : (
                    <Badge variant="outline" className={i.active ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-slate-300 text-slate-500"}>
                      {i.active ? "Aktif" : "Nonaktif"}
                    </Badge>
                  )}
                </TableCell>
                {!isViewOnly && (
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => setForm({ ...i })} data-testid={`edit-item-${i.barcode}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(i.id)} className="text-rose-500"><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-8">Tidak ada barang</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {/* Item form */}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{form?.id ? "Edit Barang" : "Tambah Barang"}</DialogTitle></DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Barcode (alfanumerik)</Label><Input value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} data-testid="item-barcode-input" /></div>
              <div className="space-y-1"><Label>Nama Barang</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="item-name-input" /></div>
              <MdSelect label="Satuan" value={form.unit} onChange={(v) => setForm({ ...form, unit: v })} options={md.units} />
              <MdSelect label="Kategori" value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={md.categories} />
              <MdSelect label="Sub-Kategori" value={form.subcategory} onChange={(v) => setForm({ ...form, subcategory: v })} options={md.subcategories} />
              <MdSelect label="Lokasi" value={form.location} onChange={(v) => setForm({ ...form, location: v })} options={md.locations} />
              <div className="space-y-1"><Label>Harga Beli</Label><RupiahInput value={form.price_buy} onChange={(v) => setForm({ ...form, price_buy: v })} /></div>
              <div className="space-y-1"><Label>Harga Jual</Label><RupiahInput value={form.price_sell} onChange={(v) => setForm({ ...form, price_sell: v })} /></div>
              <div className="space-y-1"><Label>Stok</Label><Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: Number(e.target.value) })} /></div>
              <div className="space-y-1"><Label>Stok Minimum</Label><Input type="number" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: Number(e.target.value) })} /></div>
              <div className="col-span-2 flex items-center gap-2 pt-1">
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /> <Label>Barang Aktif</Label>
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={save} className="bg-orange-500 hover:bg-orange-600" data-testid="save-item-button">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <BulkDialog open={bulkOpen} setOpen={setBulkOpen} subs={md.subcategories} onDone={() => { load(); refreshLowStock(); }} />
      <ClassDialog open={classOpen} setOpen={setClassOpen} md={md} reload={loadMd} isMaster={isMaster} />
      <ImportDialog open={importOpen} setOpen={setImportOpen} onDone={() => { load(); loadMd(); refreshLowStock(); }} />
    </div>
  );
}

function ImportDialog({ open, setOpen, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = () => {
    const headers = ["Kode Barang", "Nama Barang", "Satuan", "Kategori", "Sub Kategori", "Lokasi", "Harga Beli", "Harga Jual", "Stok", "Stok Minimum"];
    const sample = ["BRG001", "Contoh Semen 50kg", "Sak", "Material", "Semen", "Gudang", "62000", "68000", "100", "20"];
    const csv = headers.join(",") + "\n" + sample.join(",") + "\n";
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "template-impor-barang.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const submit = async () => {
    if (!file) { toast.error("Pilih file terlebih dahulu"); return; }
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/items/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Impor selesai: ${data.added} baru, ${data.updated} diperbarui`);
      onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setFile(null); setResult(null); } }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Upload className="h-4 w-4" /> Impor Barang Massal</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-500">Unggah file <b>.xlsx</b>, <b>.csv</b>, atau <b>.docx</b> (tabel). Kolom: Kode Barang, Nama Barang, Satuan, Kategori, Sub Kategori, Lokasi, Harga Beli, Harga Jual, Stok, Stok Minimum. Barang yang kodenya sudah ada akan <b>diperbarui</b>, sisanya ditambahkan.</p>
          <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-template-button"><Download className="h-4 w-4 mr-1.5" /> Unduh Template CSV</Button>
          <input type="file" accept=".xlsx,.csv,.docx" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="import-file-input"
            className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-orange-500 file:text-white file:cursor-pointer hover:file:bg-orange-600" />
          {result && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm" data-testid="import-result">
              Berhasil: <b>{result.added}</b> barang baru, <b>{result.updated}</b> diperbarui (total {result.total}).
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} className="bg-orange-500 hover:bg-orange-600" data-testid="submit-import-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Impor Sekarang"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MdSelect({ label, value, onChange, options }) {
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <Select value={value || undefined} onValueChange={onChange}>
        <SelectTrigger><SelectValue placeholder={`Pilih ${label}`} /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o.id} value={o.name}>{o.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function BulkDialog({ open, setOpen, subs, onDone }) {
  const [f, setF] = useState({ subcategory: "", mode: "percent", value: 0, target: "price_sell" });
  const submit = async () => {
    try {
      const { data } = await api.post("/items/bulk-price-update", f);
      toast.success(`${data.updated} barang diperbarui`);
      setOpen(false); onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle>Update Harga Massal</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label>Sub-Kategori</Label>
            <Select value={f.subcategory || undefined} onValueChange={(v) => setF({ ...f, subcategory: v })}>
              <SelectTrigger data-testid="bulk-subcategory-select"><SelectValue placeholder="Pilih" /></SelectTrigger>
              <SelectContent>{subs.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label>Target</Label>
            <Select value={f.target} onValueChange={(v) => setF({ ...f, target: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="price_sell">Harga Jual</SelectItem><SelectItem value="price_buy">Harga Beli</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label>Mode</Label>
            <Select value={f.mode} onValueChange={(v) => setF({ ...f, mode: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="percent">Persentase (%)</SelectItem><SelectItem value="amount">Nominal (Rp)</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label>Nilai {f.mode === "percent" ? "(%)" : "(Rp)"}</Label>
            {f.mode === "percent" ? <Input type="number" value={f.value} onChange={(e) => setF({ ...f, value: Number(e.target.value) })} /> : <RupiahInput value={f.value} onChange={(v) => setF({ ...f, value: v })} />}
          </div>
        </div>
        <DialogFooter><Button onClick={submit} className="bg-orange-500 hover:bg-orange-600" data-testid="bulk-apply-button">Terapkan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ClassDialog({ open, setOpen, md, reload, isMaster }) {
  const groups = [["units", "Satuan"], ["categories", "Kategori"], ["subcategories", "Sub-Kategori"], ["locations", "Lokasi"]];
  const [inputs, setInputs] = useState({});
  const [editing, setEditing] = useState(null); // { coll, id }
  const [editVal, setEditVal] = useState("");
  const add = async (coll) => {
    const name = (inputs[coll] || "").trim();
    if (!name) return;
    await api.post(`/${coll}`, { name });
    setInputs({ ...inputs, [coll]: "" }); reload();
  };
  const del = async (coll, id) => { await api.delete(`/${coll}/${id}`); reload(); };
  const startEdit = (coll, o) => { setEditing({ coll, id: o.id }); setEditVal(o.name); };
  const cancelEdit = () => { setEditing(null); setEditVal(""); };
  const saveEdit = async () => {
    const name = editVal.trim();
    if (!name) return;
    try {
      await api.put(`/${editing.coll}/${editing.id}`, { name });
      toast.success("Klasifikasi diperbarui");
      cancelEdit(); reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Tags className="h-4 w-4" /> Kelola Klasifikasi</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          {groups.map(([coll, label]) => (
            <div key={coll} className="border border-slate-200 rounded-lg p-3">
              <div className="font-semibold text-sm mb-2">{label}</div>
              <div className="flex gap-1.5 mb-2">
                <Input value={inputs[coll] || ""} onChange={(e) => setInputs({ ...inputs, [coll]: e.target.value })} placeholder={`Tambah ${label}`} className="h-8" data-testid={`class-add-input-${coll}`} />
                <Button size="sm" onClick={() => add(coll)} className="bg-orange-500 hover:bg-orange-600 h-8" data-testid={`class-add-button-${coll}`}><Plus className="h-3.5 w-3.5" /></Button>
              </div>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {md[coll].map((o) => (
                  <div key={o.id} className="flex items-center justify-between gap-2 text-sm bg-slate-50 px-2 py-1 rounded" data-testid={`class-row-${coll}-${o.id}`}>
                    {editing && editing.coll === coll && editing.id === o.id ? (
                      <>
                        <Input value={editVal} onChange={(e) => setEditVal(e.target.value)} className="h-7 flex-1"
                          onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") cancelEdit(); }}
                          autoFocus data-testid={`class-edit-input-${coll}`} />
                        <button onClick={saveEdit} className="text-emerald-600" data-testid={`class-save-${coll}-${o.id}`}><Check className="h-3.5 w-3.5" /></button>
                        <button onClick={cancelEdit} className="text-slate-400"><X className="h-3.5 w-3.5" /></button>
                      </>
                    ) : (
                      <>
                        <span className="truncate">{o.name}</span>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {isMaster && <button onClick={() => startEdit(coll, o)} className="text-slate-500 hover:text-orange-500" data-testid={`class-edit-${coll}-${o.id}`}><Pencil className="h-3.5 w-3.5" /></button>}
                          <button onClick={() => del(coll, o.id)}><Trash2 className="h-3.5 w-3.5 text-rose-400" /></button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
